import { Grid } from 'semantic-ui-react';
import { EpRoleDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from 'ts-debounce';
import { FormState } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { noop } from 'utils/functionUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import LoaderComponent from 'components/LoaderComponent';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import { deleteEpRole, findEpRoleById, searchEpRole } from 'services/epRoleServices';
import UpsertEpRoleForm from 'components/user-settings/UpsertEpRoleForm';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import SearchResultComponent from 'components/SearchResultComponent';

interface SearchEpRoleQuery {
  query: string;
}

interface EpRoleFieldProps extends FormState {
  values: Partial<SearchEpRoleQuery>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundEpRoles: Array<EpRoleDto>,
  epRolesLoaded: boolean,
  epRole?: EpRoleDto,
  roleId?: string,
  addEditModalOpen: boolean,
  errorMessage?: string,
  searchValues: string,
  cancelTokenSource: CancelTokenSource,
}

class EpRolesView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundEpRoles: [],
      epRolesLoaded: false,
      epRole: undefined,
      roleId: undefined,
      searchValues: "",
      addEditModalOpen: false,
      cancelTokenSource
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (roleId?: string) => {

    const {cancelTokenSource} = this.state;

    if (roleId) {
      findEpRoleById(roleId, cancelTokenSource).then(response => {
        this.showModal(roleId, response);
      }).catch(noop);
    }
  };

  showModal = (roleId?: string, epRole?: EpRoleDto) => {

    this.setState({
      addEditModalOpen: true,
      epRole: epRole,
      roleId: roleId
    });
  };

  hideModal = () => {

    this.setState({
      addEditModalOpen: false,
      roleId: undefined,
      epRole: undefined
    });
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    searchEpRole(searchValues, cancelTokenSource).then(response => {
      this.setState({
        foundEpRoles: response,
        epRolesLoaded: true
      });

      this.hideModal();
    }).catch(this.handleError);

  };

  deleteEpRole = (roleId: string): void => {
    const {cancelTokenSource} = this.state;
    deleteEpRole(roleId, cancelTokenSource).then(response => {
      this.setState(({foundEpRoles}: State) => ({
        foundEpRoles: foundEpRoles.filter((epRole: EpRoleDto) => epRole.roleId != roleId)
      }))
    }).catch(this.handleError)
  };

  handleEpRoleChange = debounce(({values}: EpRoleFieldProps) => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    this.setState({epRolesLoaded: false});
    const query = values && values.query ? values.query : "";
    searchEpRole(query, cancelTokenSource).then(response => {
      this.setState({
        foundEpRoles: response,
        epRolesLoaded: true,
        searchValues: query
      });
    }).catch(this.handleError);
  }, 300);

  handleError() {
    const {getMessage} = this.props;

    const errorMessage = getMessage('error.general');
    this.setErrorMessage(errorMessage)
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {
    const {epRolesLoaded, errorMessage} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment>
          {errorMessage
          && (
              <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                {errorMessage}
              </StyledErrorMessage>
          )
          }
          <div>
            {this.renderSearchEpRolesSegment()}
          </div>
          <StyledViewTableRow>
            {!epRolesLoaded
                ? <LoaderComponent message={getMessage("epRole.loading")}/>
                : this.renderEpRolesSegment()}
          </StyledViewTableRow>
          {this.renderUpsertEpRoleModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchEpRolesSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{}}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundEpRoles} = this.state;

    const numResults = foundEpRoles.length;

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("search")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="query"
                  fluid
                  component={Input}
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleEpRoleChange}/>
        </PxGrid>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("epRole.confirmDelete")}
              onConfirm={() => this.deleteEpRole(cellData)}
          />
        </React.Fragment>
    );
  };

  epRolesRowGetter = ({index}: any) => {
    const {foundEpRoles} = this.state;

    Object.assign(foundEpRoles[index], {index: index + 1});

    return foundEpRoles[index]
  };

  foundEpRolesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundEpRoles} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundEpRoles[index].roleId)}
        >
          {columns}
        </div>
    );
  };

  renderEpRolesSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundEpRoles} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundEpRoles.length}
            rowGetter={this.epRolesRowGetter}
            rowRenderer={this.foundEpRolesRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 450,
                label: (getMessage("roleId")),
                dataKey: "roleId"
              },
              {
                width: 450,
                label: (getMessage("name")),
                dataKey: "name"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "roleId",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderUpsertEpRoleModal = (): React.ReactNode => {

    const {addEditModalOpen, epRole, roleId} = this.state;

    return (
        addEditModalOpen &&
        <UpsertEpRoleForm
            epRole={epRole}
            roleId={roleId}
            modalOpen={addEditModalOpen}
            onCancel={this.hideModal}
            onSave={this.onSaveUpsertForm}
        />
    );
  }

}

export default withMessages(EpRolesView);
